@if (templateData(); as data) {
  <!-- Shared content for labeled styles (basic, raised, flat, stroked, extended fab).
       The optional iconClass context adds the fab-specific override classes. -->
  <ng-template #labeledContent let-iconClass>
    @if (data.hasLabel) {
      @if (data.hasIconPrefix) {
        <mat-icon class="icon-prefix" [class]="iconClass" [svgIcon]="data.iconPrefixName" [attr.aria-hidden]="true" />
      }
      {{ data.label }}
      @if (data.hasIconPostfix) {
        <mat-icon class="icon-postfix" [class]="iconClass" [svgIcon]="data.iconPostfixName" [attr.aria-hidden]="true" iconPositionEnd />
      }
    }
  </ng-template>

  <!-- Shared content for icon-only styles (icon, mini-fab, non-extended fab) -->
  <ng-template #iconContent let-iconClass>
    @if (data.hasIconPrefix) {
      <mat-icon [class]="iconClass" [svgIcon]="data.iconPrefixName" [attr.aria-hidden]="true" />
    }
  </ng-template>

  <!-- Link buttons render as anchors -->
  @if (data.type === 'link') {
    @switch (data.style) {
      @case ('basic') {
        <a
          mat-button
          [attr.href]="data.linkHref"
          role="button"
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </a>
      }
      @case ('raised') {
        <a
          mat-raised-button
          [attr.href]="data.linkHref"
          role="button"
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </a>
      }
      @case ('flat') {
        <a
          mat-flat-button
          [attr.href]="data.linkHref"
          role="button"
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </a>
      }
      @case ('stroked') {
        <a
          mat-stroked-button
          [attr.href]="data.linkHref"
          role="button"
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </a>
      }
      @case ('icon') {
        <a
          mat-icon-button
          [attr.href]="data.linkHref"
          role="button"
          [attr.aria-label]="data.ariaLabel || data.tooltip"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="iconContent" />
        </a>
      }
      @case ('fab') {
        <a
          mat-fab
          [attr.href]="data.linkHref"
          role="button"
          [attr.aria-label]="data.ariaLabel || data.tooltip"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
          [extended]="data.extended"
        >
          @if (data.hasLabel) {
            <ng-container [ngTemplateOutlet]="labeledContent" [ngTemplateOutletContext]="{ $implicit: 'fab-extended-icon-override' }" />
          } @else {
            <ng-container [ngTemplateOutlet]="iconContent" [ngTemplateOutletContext]="{ $implicit: 'fab-icon-override' }" />
          }
        </a>
      }
      @case ('mini-fab') {
        <a
          mat-mini-fab
          [attr.href]="data.linkHref"
          role="button"
          [attr.aria-label]="data.ariaLabel || data.tooltip"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="iconContent" />
        </a>
      }
    }
  }
  <!-- Button types use a <button> tag to execute local JS -->
  @else {
    @switch (data.style) {
      @case ('basic') {
        <button
          mat-button
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </button>
      }
      @case ('raised') {
        <button
          mat-raised-button
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </button>
      }
      @case ('flat') {
        <button
          mat-flat-button
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </button>
      }
      @case ('stroked') {
        <button
          mat-stroked-button
          [attr.aria-label]="data.ariaLabel || null"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="labeledContent" />
        </button>
      }
      @case ('icon') {
        <button
          mat-icon-button
          [attr.aria-label]="data.ariaLabel || data.tooltip"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="iconContent" />
        </button>
      }
      @case ('fab') {
        <button
          mat-fab
          [attr.aria-label]="data.ariaLabel || data.tooltip"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
          [extended]="data.extended"
        >
          @if (data.hasLabel) {
            <ng-container [ngTemplateOutlet]="labeledContent" [ngTemplateOutletContext]="{ $implicit: 'fab-extended-icon-override' }" />
          } @else {
            <ng-container [ngTemplateOutlet]="iconContent" [ngTemplateOutletContext]="{ $implicit: 'fab-icon-override' }" />
          }
        </button>
      }
      @case ('mini-fab') {
        <button
          mat-mini-fab
          [attr.aria-label]="data.ariaLabel || data.tooltip"
          [attr.tabindex]="data.tabIndex"
          [matTooltip]="data.tooltip"
          [matTooltipPosition]="data.tooltipPosition"
          [class.mat-button-textured]="data.isTextured"
          (click)="onClick($event)"
          (keydown)="onKeyDown($event)"
          [disabled]="data.disabled"
        >
          <ng-container [ngTemplateOutlet]="iconContent" />
        </button>
      }
    }
  }
}
